import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

const FAVORITES_STORAGE_KEY = 'manhwaRecapFavorites';

// Define the return type for the hook
export interface UseFavoritesReturn {
  favoriteIds: number[];
  addFavorite: (recapId: number) => void;
  removeFavorite: (recapId: number) => void;
  isFavorite: (recapId: number) => boolean;
  toggleFavorite: (recapId: number) => void;
}

export const useFavorites = (): UseFavoritesReturn => {
  const [favoriteIds, setFavoriteIds] = useState<number[]>(() => {
    try {
      const storedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return storedFavorites ? JSON.parse(storedFavorites) : [];
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favoriteIds));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }, [favoriteIds]);

  // Re-sync state from localStorage when page is shown from bfcache
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        try {
          const storedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
          setFavoriteIds(storedFavorites ? JSON.parse(storedFavorites) : []);
        } catch (error) {
          console.error('Error re-syncing favorites from localStorage:', error);
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const addFavorite = useCallback((recapId: number) => {
    setFavoriteIds(prevIds => {
      if (!prevIds.includes(recapId)) {
        return [...prevIds, recapId];
      }
      return prevIds;
    });
  }, []);

  const removeFavorite = useCallback((recapId: number) => {
    setFavoriteIds(prevIds => prevIds.filter(id => id !== recapId));
  }, []);

  const isFavorite = useCallback((recapId: number): boolean => {
    return favoriteIds.includes(recapId);
  }, [favoriteIds]);

  const toggleFavorite = useCallback((recapId: number) => {
    if (isFavorite(recapId)) {
      removeFavorite(recapId);
      toast.success('Removed from Favorites');
    } else {
      addFavorite(recapId);
      toast.success('Marked as Favorite');
    }
  }, [isFavorite, addFavorite, removeFavorite]);

  return {
    favoriteIds,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
  };
};
